import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import { Button, PasswordInput } from '@column/column-ui-kit';

import { NotificationList, PasswordStrength } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { ROUTE } from '~/public/routes';
import { UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Headline } from '~/styles';
import { log } from '~/util';
import { reCaptchaCheck } from '~/util/reCaptcha';

import { HintLink, Logo, Panel } from './_components';

interface Params {
  code: string;
}

export const PageNewPassword: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const { control, getValues, handleSubmit } = useForm();
  const { code } = useParams<keyof Params>() as Params;

  const { handleReCaptcha } = reCaptchaCheck();

  const equal = () => {
    return getValues('password') === getValues('repeat-password');
  };

  const onSuccess = () => {
    setLoading(true);

    handleReCaptcha(
      (reCaptchaToken: string) =>
        UserRepository.passwordReset(getValues('password'), code, reCaptchaToken)
          .then(() => {
            setLoading(false);
            log({
              name: 'User password request changed',
              context: {
                code,
              },
            });
            navigate(ROUTE.LOGIN);
            addSuccessNotification({
              content: 'Password changed',
              display: 'page-login',
            });
          })
          .catch((error) => {
            setLoading(false);
            addDangerNotification({
              content: error.message,
              display: 'page-login',
            });
          }),
      (err) => {
        setLoading(false);

        addDangerNotification({
          content: err.message,
          display: 'page-login',
        });
      }
    );
  };

  return (
    <Panel>
      <Logo size="24px" />
      <Headline fullWidth size="small">
        New password
      </Headline>
      <form onSubmit={handleSubmit(onSuccess)}>
        <NotificationList display="page-login" />
        <Flex flexDirection="column" gap={16} mb={24}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field, fieldState: { error, isTouched } }) => (
              <PasswordStrength hasError={isTouched && !!error} {...field} />
            )}
          />
          <Controller
            name="repeat-password"
            control={control}
            defaultValue=""
            rules={{ required: true, validate: equal }}
            render={({ field, fieldState: { error, isTouched } }) => (
              <PasswordInput placeholder="Repeat" hasError={isTouched && !!error} {...field} />
            )}
          />
        </Flex>
        <Flex flexDirection="column" gap={16}>
          <Button isLoading={loading}>Save</Button>
          <HintLink as={Link} to={ROUTE.LOGIN}>
            Back to sign in
          </HintLink>
        </Flex>
      </form>
    </Panel>
  );
};
